/**
 * pushApi  – Step 3
 *
 * Thin Supabase data-layer for the push_subscriptions table.
 *
 * Table schema (see migration):
 *   push_subscriptions(id, user_id, endpoint, p256dh, auth, created_at)
 *
 * RLS: users can only read/write their own rows.
 */

import { supabase } from '../auth/supabaseClient';

export interface PushSubscriptionRow {
  endpoint: string;
  p256dh: string;
  auth: string;
}

/**
 * Upserts a push subscription for the current user.
 * Uses endpoint as the unique key so refreshed subscriptions overwrite old ones.
 */
export async function upsertPushSubscription(
  userId: string,
  sub: PushSubscription,
): Promise<void> {
  const keys = sub.toJSON().keys ?? {};
  const p256dh = keys['p256dh'] ?? '';
  const auth   = keys['auth']   ?? '';

  if (!p256dh || !auth) {
    throw new Error('push subscription missing keys');
  }

  const { error } = await supabase.from('push_subscriptions').upsert(
    {
      user_id:  userId,
      endpoint: sub.endpoint,
      p256dh,
      auth,
    },
    { onConflict: 'user_id,endpoint' },
  );

  if (error) throw error;
}

/**
 * Removes a specific push subscription (e.g. on logout or explicit unsubscribe).
 */
export async function deletePushSubscription(
  userId: string,
  endpoint: string,
): Promise<void> {
  const { error } = await supabase
    .from('push_subscriptions')
    .delete()
    .eq('user_id', userId)
    .eq('endpoint', endpoint);

  if (error) throw error;
}
